import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { projectsAPI, adminAPI } from '../../services/api';

// Project options organized by type and category
const PROJECT_OPTIONS = {
    mini: {
        DEVELOPERS: [
            'Smart ATS Resume Analyzer & Optimizer',
            'AI-Powered Portfolio Generator',
            'Smart Complaint & Ticket Management System',
            'Role-Based E-Learning Platform',
            'WhatsApp Business Automation Tool',
        ],
        DATA_ANALYST: [
            'Sales & Customer Insights Dashboard',
            'Job Market Trend Analyzer',
        ],
        AI_ML: [
            'AI Resume Score Predictor',
            'Face Emotion Detection System',
            'Fake News Detection Using ML',
        ],
    },
    major: {
        DEVELOPERS: [
            'AI Job Application Automation',
            'Multi Vendor Ecommerce',
            'Smart College Management System',
            'Stock Market Simulation',
            'SaaS No Code Website Builder',
        ],
        DATA_ANALYST: [
            'EV Vehicle Dashboard',
            'Blinkit Sales Dashboard',
        ],
        AI_ML: [
            'AI Interview Evaluation System',
            'Intelligent Video Surveillance',
            'AI Content Moderation System',
        ],
    },
};

function ManageProjects() {
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newProjectData, setNewProjectData] = useState({
        name: '',
        category: 'DEVELOPERS',
        type: 'mini',
        description: '',
        downloadLink: '',
        driveLink: '',
        steps: '',
        interviewQuestions: '',
    });

    // Get available project options based on current category and type
    const getProjectOptions = useCallback(() => {
        const { type, category } = newProjectData;
        return PROJECT_OPTIONS[type]?.[category] || [];
    }, [newProjectData]);
    const [formData, setFormData] = useState({
        description: '',
        downloadLink: '',
        driveLink: '',
        steps: '',
        interviewQuestions: '',
    });
    const [uploadingDoc, setUploadingDoc] = useState(false);

    const fetchProjects = useCallback(async () => {
        try {
            const response = await projectsAPI.getProjects();
            setProjects(response.data);
        } catch (error) {
            toast.error('Failed to load projects');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    const handleProjectSelect = async (project) => {
        setSelectedProject(project);
        setShowCreateForm(false); // Close create form when selecting existing project
        setFormData({
            description: project.description || '',
            downloadLink: project.downloadLink || '',
            driveLink: project.driveLink || '',
            steps: project.steps?.map((s, i) => `Step${i + 1}: ${s.description}`).join('\n') || '',
            interviewQuestions: project.interviewQuestions?.map((q, i) => `Q${i + 1}: ${q.question}\nA: ${q.answer}`).join('\n\n') || '',
        });
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const parseSteps = (stepsText) => {
        const lines = stepsText.split('\n').filter((line) => line.trim());
        return lines.map((line, index) => {
            // New format: Step1: Description
            const match = line.match(/^Step\d+:\s*(.+)$/);
            if (match) {
                return {
                    stepNumber: index + 1,
                    title: `Step ${index + 1}`,
                    description: match[1].trim(),
                };
            }
            return {
                stepNumber: index + 1,
                title: `Step ${index + 1}`,
                description: line.trim(),
            };
        });
    };

    const parseQuestions = (questionsText) => {
        const blocks = questionsText.split(/\n\n+/).filter((block) => block.trim());
        return blocks.map((block) => {
            const lines = block.split('\n');
            const questionLine = lines.find((l) => l.startsWith('Q'));
            const answerLine = lines.find((l) => l.startsWith('A:'));

            return {
                question: questionLine ? questionLine.replace(/^Q\d+:\s*/, '').trim() : '',
                answer: answerLine ? answerLine.replace(/^A:\s*/, '').trim() : '',
            };
        });
    };

    const handleNewProjectChange = (e) => {
        const { name, value } = e.target;

        // If category or type changes, reset the project name
        if (name === 'category' || name === 'type') {
            setNewProjectData({
                ...newProjectData,
                [name]: value,
                name: '', // Reset project name when category/type changes
            });
        } else {
            setNewProjectData({
                ...newProjectData,
                [name]: value,
            });
        }
    };

    const handleCreateProject = async (e) => {
        e.preventDefault();

        if (!newProjectData.name || !newProjectData.category || !newProjectData.type) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            const projectData = {
                name: newProjectData.name,
                category: newProjectData.category,
                type: newProjectData.type,
                description: newProjectData.description,
                downloadLink: newProjectData.downloadLink,
                driveLink: newProjectData.driveLink,
                steps: parseSteps(newProjectData.steps),
                interviewQuestions: parseQuestions(newProjectData.interviewQuestions),
            };

            const response = await adminAPI.createProject(projectData);
            toast.success('Project created successfully!');

            // Reset form
            setNewProjectData({
                name: '',
                category: 'DEVELOPERS',
                type: 'mini',
                description: '',
                downloadLink: '',
                driveLink: '',
                steps: '',
                interviewQuestions: '',
            });
            setShowCreateForm(false);

            // Refresh projects list
            fetchProjects();

            // Select the newly created project
            handleProjectSelect(response.data);
        } catch (error) {
            toast.error('Failed to create project');
        }
    };

    const handleUpdateProject = async (e) => {
        e.preventDefault();

        if (!selectedProject) return;

        try {
            const updateData = {
                description: formData.description,
                downloadLink: formData.downloadLink,
                driveLink: formData.driveLink,
                steps: parseSteps(formData.steps),
                interviewQuestions: parseQuestions(formData.interviewQuestions),
            };

            await adminAPI.updateProject(selectedProject._id, updateData);
            toast.success('Project updated successfully!');
            fetchProjects();
        } catch (error) {
            toast.error('Failed to update project');
        }
    };

    const handleDocumentationUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !selectedProject) return;

        setUploadingDoc(true);
        const formData = new FormData();
        formData.append('documentation', file);

        try {
            await adminAPI.uploadDocumentation(selectedProject._id, formData);
            toast.success('Documentation uploaded successfully!');
            fetchProjects();
        } catch (error) {
            toast.error('Failed to upload documentation');
        } finally {
            setUploadingDoc(false);
        }
    };

    if (loading) {
        return <div className="loading">Loading projects...</div>;
    }

    return (
        <div className="component-container">
            <h2>Manage Projects</h2>

            <div className="create-project-section">
                <button
                    className="btn btn-success"
                    onClick={() => {
                        setShowCreateForm(!showCreateForm);
                        setSelectedProject(null);
                    }}
                >
                    {showCreateForm ? 'Cancel' : '+ Create New Project'}
                </button>

                {showCreateForm && (
                    <form onSubmit={handleCreateProject} className="create-project-form">
                        <h3>Create New Project</h3>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="category">Category *</label>
                                <select
                                    id="category"
                                    name="category"
                                    value={newProjectData.category}
                                    onChange={handleNewProjectChange}
                                    required
                                >
                                    <option value="DEVELOPERS">DEVELOPERS</option>
                                    <option value="DATA_ANALYST">DATA ANALYST</option>
                                    <option value="AI_ML">AI/ML</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Type *</label>
                                <div className="radio-group">
                                    <label>
                                        <input
                                            type="radio"
                                            name="type"
                                            value="mini"
                                            checked={newProjectData.type === 'mini'}
                                            onChange={handleNewProjectChange}
                                        />
                                        Mini Project
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="type"
                                            value="major"
                                            checked={newProjectData.type === 'major'}
                                            onChange={handleNewProjectChange}
                                        />
                                        Major Project
                                    </label>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="name">Project Name *</label>
                                <select
                                    id="name"
                                    name="name"
                                    value={newProjectData.name}
                                    onChange={handleNewProjectChange}
                                    required
                                >
                                    <option value="">-- Select a Project --</option>
                                    {getProjectOptions().map((projectName) => (
                                        <option key={projectName} value={projectName}>
                                            {projectName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="description">Description</label>
                            <textarea
                                id="description"
                                name="description"
                                value={newProjectData.description}
                                onChange={handleNewProjectChange}
                                rows="3"
                                placeholder="Enter project description"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="driveLink">Google Drive Link (Optional)</label>
                            <input
                                type="url"
                                id="driveLink"
                                name="driveLink"
                                value={newProjectData.driveLink}
                                onChange={handleNewProjectChange}
                                placeholder="https://drive.google.com/..."
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="downloadLink">Download Link (Optional)</label>
                            <input
                                type="url"
                                id="downloadLink"
                                name="downloadLink"
                                value={newProjectData.downloadLink}
                                onChange={handleNewProjectChange}
                                placeholder="https://example.com/project.zip"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="newSteps">
                                Project Steps (Format: "Step1: Description" on each line)
                            </label>
                            <textarea
                                id="newSteps"
                                name="steps"
                                value={newProjectData.steps}
                                onChange={handleNewProjectChange}
                                rows="8"
                                placeholder="Step1: Install required dependencies&#10;Step2: Configure MongoDB connection&#10;Step3: Deploy the application"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="newQuestions">
                                Interview Questions (10 questions, format: Q1: Question \n A: Answer)
                            </label>
                            <textarea
                                id="newQuestions"
                                name="interviewQuestions"
                                value={newProjectData.interviewQuestions}
                                onChange={handleNewProjectChange}
                                rows="15"
                                placeholder="Q1: What is React?&#10;A: A JavaScript library&#10;&#10;Q2: What is Node.js?&#10;A: A JavaScript runtime"
                            />
                        </div>

                        <button type="submit" className="btn btn-primary">
                            Create Project
                        </button>
                    </form>
                )}
            </div>

            <div className="projects-manager">
                <div className="projects-list">
                    <h3>Select Project</h3>
                    {['DEVELOPERS', 'DATA_ANALYST', 'AI_ML'].map((category) => (
                        <div key={category} className="category-group">
                            <h4>{category.replace('_', ' ')}</h4>
                            {projects
                                .filter((p) => p.category === category)
                                .map((project) => (
                                    <button
                                        key={project._id}
                                        className={`project-item ${selectedProject?._id === project._id ? 'active' : ''}`}
                                        onClick={() => handleProjectSelect(project)}
                                    >
                                        <span className={`badge badge-${project.type}`}>{project.type}</span>
                                        {project.name}
                                    </button>
                                ))}
                        </div>
                    ))}
                </div>

                <div className="project-editor">
                    {selectedProject ? (
                        <>
                            <h3>{selectedProject.name}</h3>
                            <p className="project-meta">
                                {selectedProject.category} - {selectedProject.type.toUpperCase()}
                            </p>

                            <form onSubmit={handleUpdateProject}>
                                <div className="form-group">
                                    <label htmlFor="description">Description</label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        rows="3"
                                        placeholder="Enter project description"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="downloadLink">Download Link</label>
                                    <input
                                        type="url"
                                        id="downloadLink"
                                        name="downloadLink"
                                        value={formData.downloadLink}
                                        onChange={handleChange}
                                        placeholder="https://example.com/project.zip"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="driveLink">Google Drive Link (Optional)</label>
                                    <input
                                        type="url"
                                        id="driveLink"
                                        name="driveLink"
                                        value={formData.driveLink}
                                        onChange={handleChange}
                                        placeholder="https://drive.google.com/..."
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="steps">
                                        Steps (Format: "Step1: Description" on each line)
                                    </label>
                                    <textarea
                                        id="steps"
                                        name="steps"
                                        value={formData.steps}
                                        onChange={handleChange}
                                        rows="8"
                                        placeholder="Step1: Install required dependencies&#10;Step2: Configure MongoDB connection&#10;Step3: Deploy application"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="interviewQuestions">
                                        Interview Questions (10 questions, format: Q1: Question \n A: Answer)
                                    </label>
                                    <textarea
                                        id="interviewQuestions"
                                        name="interviewQuestions"
                                        value={formData.interviewQuestions}
                                        onChange={handleChange}
                                        rows="15"
                                        placeholder="Q1: What is React?&#10;A: A JavaScript library&#10;&#10;Q2: What is Node.js?&#10;A: A JavaScript runtime"
                                    />
                                </div>

                                <button type="submit" className="btn btn-primary">
                                    Update Project Details
                                </button>
                            </form>

                            <div className="documentation-upload">
                                <h4>Upload Documentation</h4>
                                <div className="upload-area">
                                    <input
                                        type="file"
                                        id="documentation"
                                        onChange={handleDocumentationUpload}
                                        accept=".pdf,.docx,.doc,.zip,.rar,.txt"
                                        disabled={uploadingDoc}
                                    />
                                    <label htmlFor="documentation" className="upload-label">
                                        {uploadingDoc ? 'Uploading...' : 'Choose File (PDF, DOCX, ZIP)'}
                                    </label>
                                    {selectedProject.documentation && (
                                        <p className="file-status">✓ Documentation uploaded</p>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="empty-state">
                            <p>Select a project to manage its details</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ManageProjects;
